import React, { createContext, useContext, useState, useEffect } from 'react';
import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { Authorizer, AuthState } from './Authorizer';

interface AuthorizerContextValue {
  interactor: Authorizer;
  authState: AuthState;
}

const AuthorizerContext = createContext<AuthorizerContextValue | null>(null);

export const AuthorizerProvider: React.FC<{ children: React.ReactNode; treeId: string }> = ({ children, treeId }) => {
  const [supabase] = useState(() => {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    if (!supabaseUrl || !supabaseKey) {
      throw new Error('Missing Supabase environment variables');
    }

    console.log('supabaseUrl', supabaseUrl);

    return createClient(supabaseUrl, supabaseKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
      },
    });
  });

  const [interactor] = useState(() => new Authorizer(supabase, treeId));
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    session: null,
    loading: true,
  });

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setAuthState({
        user: session?.user ?? null,
        session,
        loading: false,
      });
    }).catch((error) => {
      console.error('Error getting initial session:', error);
      setAuthState(prev => ({ ...prev, loading: false }));
    });

    // Listen for auth state changes directly from Supabase
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state change:', event, session?.user?.email);
        setAuthState({
          user: session?.user ?? null,
          session,
          loading: false,
        });
      }
    );

    // Cleanup subscription on unmount
    return () => {
      subscription.unsubscribe();
    };
  }, [supabase]);

  return (
    <AuthorizerContext.Provider value={{ interactor, authState }}>
      {children}
    </AuthorizerContext.Provider>
  );
};

export function useAuthorizer(): Authorizer {
  const ctx = useContext(AuthorizerContext);
  if (!ctx) {
    throw new Error('useAuthorizer must be used within an AuthorizerProvider');
  }
  return ctx.interactor;
}

export function useAuthorizerContext(): AuthorizerContextValue {
  const ctx = useContext(AuthorizerContext);
  if (!ctx) {
    throw new Error('useAuthorizerContext must be used within an AuthorizerProvider');
  }
  return ctx;
}

// Convenience hook that provides the same interface as the old useAuth
export function useAuth() {
  const { interactor, authState } = useAuthorizerContext();
  
  return {
    user: authState.user,
    session: authState.session,
    loading: authState.loading,
    signInWithEmail: (email: string, redirectTo?: string) => interactor.signInWithEmail(email, redirectTo),
    signOut: interactor.signOut.bind(interactor),
    supabase: interactor.getSupabase(),
  };
}
